import { Icons, useAuth } from "@app/shared";
import { FC, PropsWithChildren, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useMedia } from "react-use";
import { urlToFilename } from "../../helpers";
import { Pages } from "../pages";
import { LATEST_TERMS_PATH } from "../pages/account/Terms";
import { AlertBell } from "../shared/AlertBell";
import { AlertsProvider } from "../../state/AlertsContext";
import { useAlertsUiVisible } from "../../hooks/useAlertsUiVisible";
import { SidebarNav, SidebarNavRef } from "./SidebarNav";

export type DashboardLayoutProps = {
    centerChildren: ReactNode;
    leftChildren?: ReactNode;
    rightChildren?: ReactNode;
    bodyClassName?: string;
} & PropsWithChildren;
export const DashboardLayout: FC<DashboardLayoutProps> = (props) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const sidebarRef = useRef<SidebarNavRef>(null);
    const alertsVisible = useAlertsUiVisible();
    // Below md the header is two rows: the title with the menu button and the alert bell, then
    // the page's own controls (the context selector on the left, actions on the right). One row
    // could not hold a two-word title, a selector and a bell at 375px — titles wrapped or clipped
    // and the bell was pushed off the edge. A media query rather than hidden duplicates, because
    // the selector fetches and caches on mount and must not be mounted twice.
    const isDesktop = useMedia("(min-width: 768px)");

    const [componentLoaded, setComponentLoaded] = useState(false);

    const hasAcceptedTerms = useMemo(() => !!auth.user?.terms?.find((x) => x.fileName === urlToFilename(LATEST_TERMS_PATH)), [auth.user]);

    useEffect(() => {
        if (auth.isLoading) { return; }
        if (!auth.isAuthenticated && !auth.isInMaintenanceMode) {
            // Preserve the originally-requested URL so login can return the user to it
            // (e.g. an alert-email deep link clicked while logged out). Login reads ?fwd= (#772).
            const fwd = encodeURIComponent(location.pathname + location.search);
            navigate(`${Pages.Login.getUrl()}?fwd=${fwd}`);
        } else if (!hasAcceptedTerms) {
            navigate(Pages.Terms.getUrl());
        }
        setComponentLoaded(true);
    }, [auth.isLoading, auth.isAuthenticated, hasAcceptedTerms, navigate, auth.isInMaintenanceMode, location.pathname, location.search])

    if (!componentLoaded) return <></>;

    return (
        <AlertsProvider>
            <div className="min-h-full h-full flex text-body-text">
                <Toaster />
                <SidebarNav ref={sidebarRef}></SidebarNav>
                <div className="bg-background-page rounded-t-lg flex-grow overflow-auto w-full md:w-auto">
                    <div className="bg-background-top-nav rounded-t-lg border-b border-separators-page">
                        <div className="flex h-14 items-center">
                            {/* mobile hamburguer button in top nav */}
                            <button onClick={() => sidebarRef.current?.openMenu()} className="md:hidden ml-4 mr-2 text-muted-body-text hover:text-body-text transition-colors flex items-center justify-center shrink-0" aria-label="Open menu">
                                <Icons.Bars className="w-6 h-6" />
                            </button>

                            {isDesktop && <div className="text-muted-body-text ml-5">{props.leftChildren}</div>}
                            <div className="flex-grow min-w-0">
                                <div className="text-base leading-tight line-clamp-2 md:line-clamp-none md:text-xl text-center px-2 md:px-0">{props.centerChildren}</div>
                            </div>
                            <div className="text-muted-body-text mr-4 md:mr-5 flex items-center shrink-0">
                                {alertsVisible && <AlertBell />}
                                {isDesktop && props.rightChildren}
                            </div>
                        </div>
                        {!isDesktop && (props.leftChildren || props.rightChildren) && (
                            <div className="flex items-center gap-3 px-4 pb-2">
                                <div className="text-muted-body-text min-w-0">{props.leftChildren}</div>
                                <div className="ml-auto flex items-center gap-2 min-w-0">{props.rightChildren}</div>
                            </div>
                        )}
                    </div>
                    {/* No min-w-fit here: the body must take its width from the viewport, not from
                        its content — content-derived width means flex-wrap containers inside never
                        see a constraint, so nothing stacks on narrow screens and the whole pane
                        scrolls instead. Content that genuinely can't shrink still overflows into
                        the parent pane's overflow-auto exactly as before. */}
                    <div className={props.bodyClassName ?? "m-5"}>{props.children}</div>
                </div>
            </div>
        </AlertsProvider>
    );
};
